import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  KeyRound,
  Copy,
  Check,
  Trash2,
  Loader2,
  Plus,
  ChevronsUpDown,
  Clock,
  CheckCircle2,
  XCircle,
  Mail,
  ExternalLink,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format, formatDistanceToNow, differenceInDays } from "date-fns";
import { pl } from "date-fns/locale";

type Token = {
  id: string;
  token: string;
  shelter_id: string;
  note: string | null;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  used_by: string | null;
  email_sent_at: string | null;
  click_count?: number | null;
  first_clicked_at?: string | null;
  last_clicked_at?: string | null;
  shelter_name?: string;
  shelter_city?: string;
  shelter_slug?: string;
};

type ShelterOption = { id: string; name: string; city: string };

const normalize = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/ł/g, "l");

const SITE_URL = "https://gdzieschronisko.pl";

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

const useTokens = () =>
  useQuery({
    queryKey: ["admin-claim-tokens"],
    queryFn: async (): Promise<Token[]> => {
      const { data, error } = await supabase
        .from("shelter_claim_tokens")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      const list = (data ?? []) as Token[];
      if (list.length === 0) return [];

      const ids = Array.from(new Set(list.map((t) => t.shelter_id)));
      const { data: shelters } = await supabase
        .from("shelters")
        .select("id, name, city, slug")
        .in("id", ids);
      const sMap = new Map((shelters ?? []).map((s) => [s.id, s]));

      return list.map((t) => ({
        ...t,
        shelter_name: sMap.get(t.shelter_id)?.name,
        shelter_city: sMap.get(t.shelter_id)?.city,
        shelter_slug: sMap.get(t.shelter_id)?.slug,
      }));
    },
  });

const useShelterOptions = () =>
  useQuery({
    queryKey: ["admin-shelter-options"],
    queryFn: async (): Promise<ShelterOption[]> => {
      const { data, error } = await supabase
        .from("shelters")
        .select("id, name, city")
        .order("name");
      if (error) throw error;
      return (data ?? []) as ShelterOption[];
    },
  });

const TokenStatus = ({ token }: { token: Token }) => {
  if (token.used_at) {
    return (
      <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 bg-emerald-50 dark:bg-emerald-950/30 dark:text-emerald-400 gap-1">
        <CheckCircle2 className="h-3 w-3" />
        Użyty {formatDistanceToNow(new Date(token.used_at), { addSuffix: true, locale: pl })}
      </Badge>
    );
  }
  const expiresAt = new Date(token.expires_at);
  const now = new Date();
  if (expiresAt < now) {
    return (
      <Badge variant="outline" className="border-destructive/40 text-destructive bg-destructive/5 gap-1">
        <XCircle className="h-3 w-3" />
        Wygasł {formatDistanceToNow(expiresAt, { addSuffix: true, locale: pl })}
      </Badge>
    );
  }
  const days = differenceInDays(expiresAt, now);
  const color =
    days <= 3
      ? "border-orange-400 text-orange-700 bg-orange-50 dark:bg-orange-950/30 dark:text-orange-400"
      : "border-primary/40 text-primary bg-primary/5";
  return (
    <Badge variant="outline" className={`${color} gap-1`}>
      <Clock className="h-3 w-3" />
      Aktywny - {days} {days === 1 ? "dzień" : days >= 2 && days <= 4 ? "dni" : "dni"} do końca
    </Badge>
  );
};

const TokenCard = ({ token }: { token: Token }) => {
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);
  const slugPart = token.shelter_slug ? `${token.shelter_slug}/` : "";
  const claimUrl = `${SITE_URL}/przejmij/${slugPart}${token.token}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(claimUrl);
      setCopied(true);
      toast.success("Link skopiowany");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Nie udało się skopiować");
    }
  };

  const deleteToken = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("shelter_claim_tokens")
        .delete()
        .eq("id", token.id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Link usunięty");
      queryClient.invalidateQueries({ queryKey: ["admin-claim-tokens"] });
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Nie udało się usunąć")),
  });

  return (
    <Card className="border-l-4 border-l-primary/40">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-sm flex items-center gap-2 flex-wrap">
              <KeyRound className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="truncate">{token.shelter_name ?? "(usunięte schronisko)"}</span>
              {token.shelter_city && (
                <span className="text-xs text-muted-foreground font-normal">
                  {token.shelter_city}
                </span>
              )}
              {token.shelter_slug && (
                <Link
                  to={`/schronisko/${token.shelter_slug}`}
                  target="_blank"
                  className="text-primary hover:underline text-xs"
                >
                  <ExternalLink className="h-3 w-3 inline" />
                </Link>
              )}
            </CardTitle>
            <CardDescription className="text-xs mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>Utworzony {format(new Date(token.created_at), "d MMM yyyy, HH:mm", { locale: pl })}</span>
              {token.email_sent_at && (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Mail className="h-3 w-3" /> wysłany mailem
                </span>
              )}
              {(token.click_count ?? 0) > 0 && (
                <span
                  className="inline-flex items-center gap-1 text-primary"
                  title={token.last_clicked_at ? `Ostatnie kliknięcie: ${format(new Date(token.last_clicked_at), "d MMM yyyy, HH:mm", { locale: pl })}` : undefined}
                >
                  <ExternalLink className="h-3 w-3" />
                  {token.click_count} {token.click_count === 1 ? "kliknięcie" : (token.click_count! >= 2 && token.click_count! <= 4 ? "kliknięcia" : "kliknięć")}
                  {token.last_clicked_at && (
                    <span className="text-muted-foreground">
                      · ostatnio {formatDistanceToNow(new Date(token.last_clicked_at), { addSuffix: true, locale: pl })}
                    </span>
                  )}
                </span>
              )}
            </CardDescription>
          </div>
          <TokenStatus token={token} />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex gap-2 items-center">
          <Input
            readOnly
            value={claimUrl}
            className="text-xs font-mono"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopy}
            disabled={!!token.used_at}
            className="shrink-0"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => deleteToken.mutate()}
            disabled={deleteToken.isPending}
            className="shrink-0 text-destructive hover:text-destructive"
          >
            {deleteToken.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
        {token.note && (
          <p className="text-xs text-muted-foreground italic">Notatka: {token.note}</p>
        )}
      </CardContent>
    </Card>
  );
};

const GenerateLinkDialog = () => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [comboOpen, setComboOpen] = useState(false);
  const [shelterId, setShelterId] = useState<string>("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const { data: shelters } = useShelterOptions();

  const selected = useMemo(
    () => shelters?.find((s) => s.id === shelterId),
    [shelters, shelterId],
  );

  const generate = useMutation({
    mutationFn: async () => {
      const body: Record<string, string> = { shelter_id: shelterId };
      if (email.trim()) body.recipient_email = email.trim();
      if (note.trim()) body.note = note.trim();
      const { data, error } = await supabase.functions.invoke("claim-shelter-token", { body });
      if (error) throw error;
      return data as { claim_url: string };
    },
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.claim_url);
        toast.success("Link wygenerowany i skopiowany do schowka");
      } catch {
        toast.success("Link wygenerowany");
      }
      queryClient.invalidateQueries({ queryKey: ["admin-claim-tokens"] });
      setOpen(false);
      setShelterId("");
      setEmail("");
      setNote("");
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, "Nie udało się wygenerować")),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Wygeneruj link
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Wygeneruj link do przejęcia</DialogTitle>
          <DialogDescription>
            Link jest ważny 30 dni. Możesz wysłać go mailem (automatycznie) lub skopiować i przekazać innym kanałem (np. Instagram DM).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Schronisko *</label>
              <Popover open={comboOpen} onOpenChange={setComboOpen} modal>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  className="w-full justify-between font-normal"
                >
                  {selected ? `${selected.name} - ${selected.city}` : "Wybierz schronisko..."}
                  <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="z-[2300] w-[--radix-popover-trigger-width] p-0" align="start">
                <Command
                  filter={(value, search) =>
                    normalize(value).includes(normalize(search)) ? 1 : 0
                  }
                >
                  <CommandInput placeholder="Szukaj..." />
                  <CommandList>
                    <CommandEmpty>Brak wyników</CommandEmpty>
                    <CommandGroup>
                      {(shelters ?? []).map((s) => (
                        <CommandItem
                          key={s.id}
                          value={`${s.name} ${s.city}`}
                          onSelect={() => {
                            setShelterId(s.id);
                            setComboOpen(false);
                          }}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setShelterId(s.id);
                            setComboOpen(false);
                          }}
                        >
                          {s.name} <span className="text-muted-foreground ml-2">{s.city}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Email (opcjonalnie)</label>
            <Input
              type="email"
              placeholder="kontakt@schronisko.pl"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Jeśli puste - link nie zostanie wysłany, tylko skopiowany do schowka.
            </p>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Notatka wewnętrzna (opcjonalnie)</label>
            <Textarea
              placeholder="np. Wysłany przez Instagram"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Anuluj
          </Button>
          <Button
            onClick={() => generate.mutate()}
            disabled={!shelterId || generate.isPending}
          >
            {generate.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            Wygeneruj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ClaimTokensSection = () => {
  const { data: tokens, isLoading } = useTokens();
  const [filter, setFilter] = useState<"all" | "active" | "used" | "expired">("all");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    const list = tokens ?? [];
    const now = new Date();
    return {
      all: list.length,
      active: list.filter((t) => !t.used_at && new Date(t.expires_at) >= now).length,
      used: list.filter((t) => !!t.used_at).length,
      expired: list.filter((t) => !t.used_at && new Date(t.expires_at) < now).length,
    };
  }, [tokens]);

  const filtered = useMemo(() => {
    const list = tokens ?? [];
    const now = new Date();
    let result = list;
    if (filter === "active") result = list.filter((t) => !t.used_at && new Date(t.expires_at) >= now);
    else if (filter === "used") result = list.filter((t) => !!t.used_at);
    else if (filter === "expired")
      result = list.filter((t) => !t.used_at && new Date(t.expires_at) < now);
    const q = normalize(search.trim());
    if (q) {
      result = result.filter((t) =>
        normalize(`${t.shelter_name ?? ""} ${t.shelter_city ?? ""}`).includes(q),
      );
    }
    return result;
  }, [tokens, filter, search]);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <h3 className="font-display font-semibold text-base">Linki do przejęcia</h3>
          <Badge variant="secondary">{counts.all}</Badge>
        </div>
        <GenerateLinkDialog />
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Szukaj schroniska po nazwie lub mieście..."
          className="pl-9 h-9"
        />
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {([
          ["all", "Wszystkie", counts.all],
          ["active", "Aktywne", counts.active],
          ["used", "Użyte", counts.used],
          ["expired", "Wygasłe", counts.expired],
        ] as const).map(([key, label, count]) => (
          <Button
            key={key}
            size="sm"
            variant={filter === key ? "default" : "outline"}
            onClick={() => setFilter(key)}
            className="h-7 text-xs"
          >
            {label} <Badge variant="secondary" className="ml-1.5 h-4 px-1.5 text-[10px]">{count}</Badge>
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Brak linków w tej kategorii.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((t) => (
            <TokenCard key={t.id} token={t} />
          ))}
        </div>
      )}
    </section>
  );
};
